import { createDeviceGroup } from 'gen/client';
import { UserCreateDeviceGroupAddress, UserDeviceGroupDTO } from 'gen/model';

import { RtcComponentError } from '@ui/Components/Backend/RtcComponent';
import { useSessionsStore } from '@ui/Components/Backend/sessionsStore';
import { TauriRtcComponent } from '@ui/Components/Backend/TauriRtcComponent';
import { UIInstance } from '@ui/Types/Address';

const errorTypes = {
    CANCEL: {
        code: 3,
        message: 'Discovery was cancelled',
    },
    INTERNAL: {
        code: 1,
        message: 'Internal error occurred, try again later',
    },
    NOT_FOUND: {
        code: 2,
        message: 'Device could not be discovered',
    },
};

export class RtcDiscoverError extends Error {
    code: number;
    detail?: string;
    componentErrors?: RtcComponentError[];
    constructor(type: keyof typeof errorTypes, message?: string, componentErrors?: RtcComponentError[]) {
        super(errorTypes[type].message);
        this.name = 'RtcDiscoverError';
        this.code = errorTypes[type].code;
        this.detail = message;
        this.componentErrors = componentErrors;
    }
}

export async function handleDiscoverRtc(instance: UIInstance, signal: AbortSignal): Promise<UserDeviceGroupDTO> {
    if (signal.aborted) {
        throw new RtcDiscoverError('CANCEL', 'Discovery was cancelled by the user');
    }

    const state = useSessionsStore.getState();
    const sessions = state.sessions;
    const rtc = state.rtc;

    if (!sessions) {
        throw new RtcDiscoverError('NOT_FOUND', 'No sessions available');
    }

    if (!sessions.arp.length && !sessions.headless.length) {
        throw new RtcDiscoverError('NOT_FOUND', 'No available ARP or HEADLESS sessions');
    }

    if (!rtc) {
        throw new RtcDiscoverError('NOT_FOUND', 'RTC component is not initialized');
    }

    if (rtc instanceof TauriRtcComponent) {
        throw new RtcDiscoverError('INTERNAL', 'TauriRtcComponent is not supported in this context');
    }

    const addresses: UserCreateDeviceGroupAddress[] = [];
    const errors: RtcComponentError[] = [];

    // Trying users arp capable devices (tauri desktop) first
    for (const session of sessions.arp) {
        try {
            const mac = await rtc.createRtc('browser', session, signal);
            if (mac) {
                addresses.push({ address: mac, bluetooth: false });
            }
        } catch (error) {
            if (error instanceof RtcComponentError) {
                if (error.code === errorTypes.CANCEL.code) {
                    throw new RtcDiscoverError('CANCEL', 'Discovery was cancelled by the user');
                }

                errors.push(error);
            } else {
                console.error('Unexpected error:', error);
            }
        }
    }

    // Trying sensors second
    for (const session of sessions.headless) {
        if (session.instance !== instance.id) {
            console.log(`Skipping session ${session.id} for instance ${instance.id}`);
            continue;
        }

        try {
            const mac = await rtc.createRtc('headless', session.id, signal);
            if (mac) {
                addresses.push({ address: mac, bluetooth: false, instance: instance.cloak_role });
            }
        } catch (error) {
            if (error instanceof RtcComponentError) {
                if (error.code === errorTypes.CANCEL.code) {
                    throw new RtcDiscoverError('CANCEL', 'Discovery was cancelled by the user');
                }

                errors.push(error);
            } else {
                console.error('Unexpected error:', error);
            }
        }
    }

    if (!addresses.length) {
        throw new RtcDiscoverError('NOT_FOUND', 'No devices found', errors);
    }

    try {
        const res = await createDeviceGroup(
            {
                addresses,
                group_name: navigator.userAgent,
                location: true,
                visible: true,
            },
            {
                signal,
            },
        );
        return res;
    } catch (error) {
        throw new RtcDiscoverError('INTERNAL', `Failed to create device group: ${String(error)}`);
    }
}
